import { Type, type Static } from "@sinclair/typebox";
import { Cents, PlaceInput, RideCategory } from "./domain.ts";
import { TripTypeView } from "./trip-types.ts";

/** Envios — package delivery (Cliente-15, Cliente-16, Motorista-17). A delivery is a trip with `kind: "delivery"`. */

export const DELIVERY_SIZES = ["p", "m", "g"] as const;
/**
 * Sizes of Envios before trip types (docs/specs/trip-types.md): only old trips carry one. New
 * deliveries pick a delivery trip type ("Envios Moto", "Envios Carro") instead.
 */
export const DeliverySize = Type.Union([Type.Literal("p"), Type.Literal("m"), Type.Literal("g")], { $id: "DeliverySize" });
export type DeliverySize = Static<typeof DeliverySize>;

export const DELIVERY_SIZE_LABELS: Record<DeliverySize, { name: string; hint: string }> = {
  p: { name: "Pequeno", hint: "Mochila · até 5 kg · moto" },
  m: { name: "Médio", hint: "Banco do carro · até 15 kg" },
  g: { name: "Grande", hint: "Porta-malas · até 30 kg" },
};

/** Suggested contents chips; any short text is accepted. */
export const DELIVERY_CONTENTS = ["Documentos", "Chaves", "Roupas", "Eletrônicos", "Presente", "Outro"] as const;

export const DeliveryQuoteRequest = Type.Object({ pickup: PlaceInput, dropoff: PlaceInput }, { $id: "DeliveryQuoteRequest" });
export const DeliveryQuoteOption = Type.Object(
  { category: RideCategory, type: TripTypeView, fareCents: Cents, pickupEtaSeconds: Type.Union([Type.Integer(), Type.Null()]) },
  { $id: "DeliveryQuoteOption" },
);
export const DeliveryQuoteResponse = Type.Object(
  {
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
    polyline: Type.Union([Type.String(), Type.Null()]),
    options: Type.Array(DeliveryQuoteOption),
  },
  { $id: "DeliveryQuoteResponse" },
);

export const DeliveryRecipientInput = Type.Object(
  { name: Type.String({ minLength: 2, maxLength: 80 }), phone: Type.String({ minLength: 10, maxLength: 20, description: "E.164 (+5511…) or a Brazilian number with DDD" }) },
  { $id: "DeliveryRecipientInput" },
);
export const DeliveryRequest = Type.Object(
  {
    /** A delivery trip type (`service: "delivery"`). */
    category: RideCategory,
    contents: Type.Array(Type.String({ minLength: 1, maxLength: 40 }), { minItems: 1, maxItems: 6 }),
    recipient: DeliveryRecipientInput,
    pickup: PlaceInput,
    dropoff: PlaceInput,
    /** "Não tem dinheiro, documentos de terceiros, … itens proibidos". */
    acceptsRules: Type.Literal(true),
  },
  { $id: "DeliveryRequest" },
);
export type DeliveryRequest = Static<typeof DeliveryRequest>;

/** `TripView.delivery`. `deliveryCode` is only present for the sender. */
export const TripDeliveryView = Type.Object(
  {
    /** Old trips only. */
    size: Type.Optional(DeliverySize),
    contents: Type.Array(Type.String()),
    /** Full name for the sender; "Carla M." for the driver. */
    recipientName: Type.String(),
    recipientPhoneMasked: Type.String(),
    deliveryCode: Type.Optional(Type.String()),
    pickupPhotoAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    dropoffPhotoAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    recipientAbsent: Type.Boolean(),
    deliveredAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "TripDeliveryView" },
);
export type TripDeliveryView = Static<typeof TripDeliveryView>;

/** What a driver sees about the package on the offer ("Entrega · Pequeno · Documentos"). */
export const OfferDelivery = Type.Object(
  { size: Type.Optional(DeliverySize), contents: Type.Array(Type.String()) },
  { $id: "OfferDelivery" },
);

/**
 * `POST /trips/{id}/complete` body. Rides send nothing. Deliveries send the recipient's code, or
 * `recipientAbsent: true` after the dropoff photo.
 */
export const CompleteRequest = Type.Object(
  { deliveryCode: Type.Optional(Type.String({ pattern: "^[0-9]{4}$" })), recipientAbsent: Type.Optional(Type.Boolean()) },
  { $id: "CompleteRequest" },
);
